import React, { useMemo } from 'react'
import { useProduct } from '@/hooks/useProduct'
import { error } from '@/services/Log'
import { FEATURE_TOGGLES } from '@/services/Configuration'
import {
  addDaysToDate,
  toEstimatedDateFormat,
  toEstimatedDateFormatWithYear,
} from '@/helpers/timeAndDateUtils'
import { checkIsBackOrder } from '@/helpers/checkIsBackOrder'
import type { ProductWarrantyVariant } from '@/types/Product'

import Text from '@csc/dls/Text'

type EstimatedArrivalProps = {
  variant: ProductWarrantyVariant
  className?: string
  finalDateOffset?: number
}

const MINIMUM_SHIP_TIME_DEFAULT = 2
const MAXIMUM_SHIP_TIME_DEFAULT_PARCEL = 6
const MAXIMUM_SHIP_TIME_DEFAULT_LTL = 12

export type ArrivalWindowFunctor = (p: {
  variant: ProductWarrantyVariant,
  baseDate?: Date
  toDateTimeRange?: boolean,
  finalDateOffset?: number,
}) => string

export const arrivalWindow: ArrivalWindowFunctor = ({
  variant,
  baseDate = new Date(),
  toDateTimeRange = false,
}) => {
  try {
    const {
      lead_time: leadTime,
      is_digital,
      is_white_glove,
      is_entryway,
    } = variant
    if (typeof leadTime !== 'number') {
      return ''
    }
    // because we do not do same-day, for now we do not need to
    // adjust by one day if it is after 5pm, say, local time.
    const isLtL = Boolean(is_white_glove || is_entryway)
    const min = leadTime + MINIMUM_SHIP_TIME_DEFAULT
    const max = leadTime + (isLtL
      ? MAXIMUM_SHIP_TIME_DEFAULT_LTL
      : MAXIMUM_SHIP_TIME_DEFAULT_PARCEL)
    const isNotDigitalAndHasMinAndMax = [min, max].every(Number.isFinite) && !is_digital
    if (isNotDigitalAndHasMinAndMax) {
      const maxDate = addDaysToDate(baseDate.toISOString(), max)
      if (toDateTimeRange) {
        return `${toEstimatedDateFormatWithYear(addDaysToDate(baseDate.toISOString(), min))}-${toEstimatedDateFormatWithYear(maxDate)}`
      }
      return `${toEstimatedDateFormat(addDaysToDate(baseDate.toISOString(), min))} - ${toEstimatedDateFormat(maxDate)}`
    }
  } catch (err) {
    error(`Failed to calculate the arrival Window ${err}`)
  }
  return ''
}

export const getVariantBaseDate = (variant: ProductWarrantyVariant) => {
  const isBackOrder = checkIsBackOrder(variant)
  const today = new Date()
  try {
    const { baseDateForDeliveryCalculation } = variant
    if (baseDateForDeliveryCalculation && isBackOrder) {
      const baseDate = new Date(baseDateForDeliveryCalculation)
      if (baseDate > today) {
        return { date: baseDate, isBackOrder }
      }
    }
  } catch (err) {
    error('Failed to base delivery date from variant', { variant })
  }
  return { date: today, isBackOrder }
}

const EstimatedArrival: React.FC<EstimatedArrivalProps> = ({
  variant,
  className = 'w-full text-center',
  finalDateOffset = 0,
}) => {
  const { product } = useProduct()
  const is_digital = !!product?.warranty.additional.is_digital

  const { date: baseDate, isBackOrder } = useMemo(() => getVariantBaseDate(variant), [variant])

  const arrival = arrivalWindow({ variant, baseDate, finalDateOffset })

  const { shouldShowBackorderBadge } = FEATURE_TOGGLES

  if (is_digital || !arrival) {
    return <div />
  }

  return (
    <Text className={className} size="sm">
      <Text size="sm" bold textTag="span" className={shouldShowBackorderBadge ? 'text-backOrder' : ''}>
        {shouldShowBackorderBadge ? 'On Backorder - ' : ''}
        Est. Arrival:
      </Text>
      <Text size="sm" textTag="span" className={`inline-block ml-1 ${isBackOrder ? 'text-backOrder' : ''}`}>
        {' '}
        {arrival}
      </Text>
    </Text>
  )
}

export default EstimatedArrival
